/**
 * Practice Session Hook
 *
 * Manages practice session state, mode launching, assessment flow,
 * and related UI state (module panel, cheat sheet, validation errors).
 *
 * Moved from src/hooks/usePracticeHandlers.ts in R31 (DDD Fase 4).
 * Intra-domain import: ../logic/sessionValidation (was ../../utils/sessionValidation)
 */

import { useState, useCallback } from 'react';
import { type ModuleId, type Question, type ModuleData, getAllQuestions } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { createModeSession } from '../../modes';
import type { FluencyConfig, SprintConfig, ChallengeConfig, ExperimentsConfig, ModeId as ModeIdType } from '../../modes';
import type { PracticeAssignment } from '../../assessment';
import { getWeakestFamiliesGlobal, getStrongestFamiliesGlobal } from '../../mastery';
import { validateSessionAvailability, logValidationReport } from '../logic/sessionValidation';
import { debug, warn, error as logError } from '../../utils/logger';
import { shuffleWithSeed } from '../../utils/deterministicShuffle';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
interface UsePracticeSessionParams {
  state: AppState;
  selectedGroup: GroupId;
  markCheatSheetViewed: (moduleId: ModuleId) => void;
  enterPracticeView: () => void;
  resetToDashboard: () => void;
  resetToLearningLab: () => void;
  /**
   * Optional callback fired when a focus session backed by a teacher
   * PracticeAssignment (huiswerk) completes. Consumer is responsible for
   * persisting completion (e.g. markPracticeAssignmentCompletedByStudent).
   */
  onAssignmentComplete?: (assignmentId: string) => void;
}
export function usePracticeSession({
  state,
  selectedGroup,
  markCheatSheetViewed,
  enterPracticeView,
  resetToDashboard,
  resetToLearningLab,
  onAssignmentComplete
}: UsePracticeSessionParams) {
  const [activeModuleId, setActiveModuleId] = useState<ModuleId | null>(null);
  const [activeModeId, setActiveModeId] = useState<string | null>(null);
  const [isFocusSession, setIsFocusSession] = useState(false);
  const [focusQuestions, setFocusQuestions] = useState<Question[]>([]);
  const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<ModuleData | null>(null);
  const [cheatSheetModuleId, setCheatSheetModuleId] = useState<ModuleId | null>(null);
  const [validationError, setValidationError] = useState<{
    moduleId: ModuleId;
    message: string;
    hasContent: boolean;
  } | null>(null);

  // Keyboard navigation (Escape to close panels)
  const handleEscapeClose = useCallback(() => {
    if (cheatSheetModuleId) setCheatSheetModuleId(null);else if (selectedModule) setSelectedModule(null);
  }, [cheatSheetModuleId, selectedModule]);
  useEscapeKey(handleEscapeClose, !!(cheatSheetModuleId || selectedModule));
  const handleStartPractice = useCallback((moduleId: ModuleId) => {
    const validationReport = validateSessionAvailability(moduleId, selectedGroup);
    if (!validationReport.isValid) {
      if (import.meta.env?.DEV) {
        warn(`Cannot start practice for ${moduleId} in group ${selectedGroup}:`);
        logValidationReport(validationReport);
      }
      setValidationError({
        moduleId,
        message: validationReport.errors[0] || 'Geen vragen beschikbaar',
        hasContent: validationReport.stats.totalFamilies > 0
      });
      return;
    }
    if (import.meta.env?.DEV) {
      debug(`Pre-flight check passed for ${moduleId} in group ${selectedGroup}`);
      debug(`   ${validationReport.stats.totalQuestionsAvailable} questions available from ${validationReport.stats.familiesWithQuestions} families`);
    }
    markCheatSheetViewed(moduleId);
    setActiveModuleId(moduleId);
    enterPracticeView();
    setIsFocusSession(false);
    setSelectedModule(null);
  }, [selectedGroup, markCheatSheetViewed, enterPracticeView]);
  const handlePracticeComplete = useCallback(() => {
    const wasFocusSession = isFocusSession;
    const completedAssignmentId = activeAssignmentId;
    setActiveModuleId(null);
    setActiveModeId(null);
    setIsFocusSession(false);
    setFocusQuestions([]);
    setActiveAssignmentId(null);
    // Fire assignment-completion callback BEFORE navigation so the
    // home banner is refreshed when the learner returns.
    if (completedAssignmentId && onAssignmentComplete) {
      onAssignmentComplete(completedAssignmentId);
    }
    // Focus sessions (modes/homework) always navigate back to the home/lab.
    if (wasFocusSession) {
      // Homework assignments originate from HomePage, not the LearningLab,
      // so route home when an assignment was active.
      if (completedAssignmentId) {
        resetToDashboard();
      } else {
        resetToLearningLab();
      }
    } else {
      resetToDashboard();
    }
  }, [isFocusSession, activeAssignmentId, onAssignmentComplete, resetToDashboard, resetToLearningLab]);
  const exitPractice = useCallback(() => {
    const wasFocusSession = isFocusSession;
    const wasAssignment = activeAssignmentId !== null;
    setActiveModuleId(null);
    setActiveModeId(null);
    setIsFocusSession(false);
    setFocusQuestions([]);
    setActiveAssignmentId(null);
    if (wasFocusSession) {
      if (wasAssignment) {
        resetToDashboard();
      } else {
        resetToLearningLab();
      }
    } else {
      resetToDashboard();
    }
  }, [isFocusSession, activeAssignmentId, resetToDashboard, resetToLearningLab]);

  // Start a focus session (assessment or mode) with pre-selected questions
  const startFocusSession = useCallback((questions: Question[]) => {
    setActiveModuleId(null);
    setIsFocusSession(true);
    setFocusQuestions(questions);
    enterPracticeView();
  }, [enterPracticeView]);

  /**
   * Start a practice session scoped to a teacher PracticeAssignment (huiswerk).
   *
   * BUG-HW-1/2 FIX: Previously HomePage called handleStartPractice with the
   * first moduleId, which ignored targetFamilyIds entirely — a student
   * assigned "tafels" would get any question from REK. This routes through
   * startFocusSession with a question pool filtered by:
   *   - assignment.moduleIds (must match)
   *   - assignment.targetFamilyIds (when non-empty; empty = whole module)
   *
   * Questions are deterministically shuffled (seeded by assignment id) and
   * capped at assignment.questionCount.
   */
  const handleStartHomeworkPractice = useCallback((assignment: PracticeAssignment) => {
    const allQuestions = getAllQuestions();
    const moduleSet = new Set(assignment.moduleIds);
    const familySet = assignment.targetFamilyIds && assignment.targetFamilyIds.length > 0 ? new Set(assignment.targetFamilyIds) : null;
    const pool = allQuestions.filter(q => {
      if (!moduleSet.has(q.module as ModuleId)) return false;
      if (familySet && !familySet.has(q.familyId)) return false;
      return true;
    });
    if (pool.length === 0) {
      warn(`[homework] Assignment ${assignment.id} has no eligible questions ` + `(modules=${assignment.moduleIds.join(',')}, families=${assignment.targetFamilyIds.join(',') || 'all'})`);
      setValidationError({
        moduleId: (assignment.moduleIds[0] || 'unknown') as ModuleId,
        message: 'Voor dit huiswerk zijn nog geen vragen beschikbaar. Vraag je leraar om hulp.',
        hasContent: false
      });
      return;
    }
    const {
      shuffled
    } = shuffleWithSeed(pool, `homework-${assignment.id}`);
    const cap = assignment.questionCount > 0 ? assignment.questionCount : 20;
    const selected = shuffled.slice(0, cap);
    if (import.meta.env?.DEV) {
      debug(`[homework] Starting assignment ${assignment.id} — ${selected.length}/${pool.length} questions ` + `(modules=${assignment.moduleIds.join(',')}, families=${assignment.targetFamilyIds.join(',') || 'all'})`);
    }
    setActiveAssignmentId(assignment.id);
    startFocusSession(selected);
  }, [startFocusSession]);
  const handleStartMode = useCallback((modeId: string, config?: {
    targetFamilyId?: string;
    targetModules?: string[];
  }) => {
    try {
      let modeConfig: FluencyConfig | SprintConfig | ChallengeConfig | ExperimentsConfig;
      switch (modeId) {
        case 'fluency':
          modeConfig = {
            duration: 300,
            targetModules: config?.targetModules || null
          } as FluencyConfig;
          break;
        case 'sprint':
          {
            let targetFamilyId = config?.targetFamilyId;
            if (!targetFamilyId) {
              const weakFamilies = getWeakestFamiliesGlobal(state.familyMastery || {}, selectedGroup, 1);
              if (weakFamilies.length === 0) {
                setValidationError({
                  moduleId: 'sprint' as ModuleId,
                  message: 'Je hebt nog geen onderwerpen geoefend. Begin eerst met een vak!',
                  hasContent: false
                });
                return;
              }
              targetFamilyId = weakFamilies[0].familyId;
            }
            modeConfig = {
              targetFamilyId,
              targetCount: 10,
              adaptive: true,
              warmup: true
            } as SprintConfig;
            break;
          }
        case 'challenge':
          {
            let targetFamilyId = config?.targetFamilyId;
            if (!targetFamilyId) {
              // FIX F-MAS-R3-CHALLENGE-MODE-CROSS-GROUP-LEAK:
              // Use group-scoped helper so Challenge never targets families
              // from a different group's mastery record.
              const strongFamilies = getStrongestFamiliesGlobal(state.familyMastery || {}, selectedGroup, 70, 1);
              if (strongFamilies.length === 0) {
                setValidationError({
                  moduleId: 'challenge' as ModuleId,
                  message: 'Je hebt nog geen onderwerpen goed genoeg beheerst (70%+). Oefen eerst meer!',
                  hasContent: false
                });
                return;
              }
              targetFamilyId = strongFamilies[0].familyId;
            }
            modeConfig = {
              targetFamilyId,
              difficulty: 2
            } as ChallengeConfig;
            break;
          }
        case 'experiments':
          modeConfig = {
            experimentType: 'mixed'
          } as ExperimentsConfig;
          break;
        case 'free':
          {
            // Free practice: load ALL questions for a family, ignoring group/tier filters
            const freeFamilyId = config?.targetFamilyId;
            if (!freeFamilyId) return;
            const allQuestions = getAllQuestions();
            const familyQuestions = allQuestions.filter(q => q.familyId === freeFamilyId);
            if (familyQuestions.length === 0) {
              setValidationError({
                moduleId: 'free' as ModuleId,
                message: 'Geen vragen beschikbaar voor dit onderwerp.',
                hasContent: false
              });
              return;
            }
            // Shuffle deterministically (seeded by family id) and take up to 10
            const shuffled = shuffleWithSeed(familyQuestions, `free-practice-${freeFamilyId}`).shuffled;
            startFocusSession(shuffled.slice(0, 10));
            return;
          }
        default:
          return;
      }
      const session = createModeSession(modeId as ModeIdType, modeConfig as FluencyConfig | SprintConfig | ChallengeConfig, state, selectedGroup);
      setActiveModeId(modeId);
      startFocusSession(session.questions);
    } catch (err) {
      logError('Failed to start mode session:', err);
      setValidationError({
        moduleId: modeId as ModuleId,
        message: 'Geen vragen beschikbaar voor deze trainingsmodus. Probeer een ander vak te oefenen.',
        hasContent: false
      });
    }
  }, [state, selectedGroup, startFocusSession]);
  return {
    activeModuleId,
    activeModeId,
    isFocusSession,
    focusQuestions,
    activeAssignmentId,
    selectedModule,
    setSelectedModule,
    cheatSheetModuleId,
    setCheatSheetModuleId,
    validationError,
    setValidationError,
    handleStartPractice,
    handleStartHomeworkPractice,
    handlePracticeComplete,
    handleStartMode,
    exitPractice,
    startFocusSession
  };
}